import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) return;
    if (formData.message.length > 500) {
      setStatus('error');
      setErrorMsg('留言内容不能超过500个字符');
      return;
    }

    setStatus('loading');
    try {
      const resp = await fetch('https://readdy.ai/api/form/d9u50plca23r0tr1aqng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (resp.ok && parsed?.code === 'OK') {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
        if (String(serverMsg).includes('spam')) {
          setStatus('success');
          setFormData({ name: '', email: '', message: '' });
        } else {
          setStatus('error');
          setErrorMsg(serverMsg);
        }
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络异常，请稍后再试');
    }
  };

  return (
    <section id="contact" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">
            联系我们
          </h2>
          <p className="text-sm text-foreground-500 leading-relaxed">
            有任何想说的，都可以写给我们。每一条留言都会被认真阅读。
          </p>
        </div>

        {status === 'success' ? (
          <div className="bg-secondary-50 border border-secondary-200/70 rounded-lg p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-check-line text-2xl text-secondary-600"></i>
            </div>
            <p className="text-foreground-800 font-medium mb-1">感谢你的留言！</p>
            <p className="text-sm text-foreground-500">我们会尽快阅读并回复。</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">称呼</label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="怎么称呼你？"
                className="w-full px-4 py-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="w-full px-4 py-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                留言
                <span className="text-foreground-400 ml-1 font-normal">（限500字符）</span>
              </label>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={5}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="写下你想说的话..."
                className="w-full px-4 py-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-all resize-none"
              />
              <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
            </div>
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {status === 'loading' ? '提交中...' : '发送留言'}
            </button>
          </form>
        )}
        {status === 'error' && (
          <p className="text-xs text-accent-500 mt-3 text-center">{errorMsg}</p>
        )}
      </div>
    </section>
  );
}